/** Typed API client. The browser talks ONLY to the API Worker (§18); cookies
 * carry the session (credentials: 'include'). */
const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const msg = (body as { error?: string } | null)?.error ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, msg);
  }
  return body as T;
}

/** POST multipart form data (no JSON content-type). */
async function postForm<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { method: 'POST', credentials: 'include', body: form });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok)
    throw new ApiError(
      res.status,
      (body as { error?: string } | null)?.error ?? `HTTP ${res.status}`,
    );
  return body as T;
}

/** Fetch a binary endpoint (with credentials) as an object URL. */
async function getObjectUrl(path: string): Promise<string> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include' });
  if (!res.ok) throw new ApiError(res.status, `HTTP ${res.status}`);
  return URL.createObjectURL(await res.blob());
}

/** Trigger a file download from a binary endpoint (with credentials). */
async function download(path: string, filename: string): Promise<void> {
  const url = await getObjectUrl(path);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface SessionUser {
  id: string;
  email: string;
  nombre: string | null;
  permissions: string[];
}

export interface KpiValue {
  value: number | null;
  comparison?: number | null;
  deltaAbs?: number | null;
  deltaPct?: number | null;
}

export interface SegmentMix {
  individual: number | null;
  company: number | null;
  travelAgent: number | null;
  block: number | null;
}

export interface RangeSummary {
  occPct: number | null;
  adrUsd: number | null;
  revparUsd: number | null;
  trevparUsd: number | null;
  roomRevenueBs: number;
  totalRevenueBs: number;
  roomRevenueUsd: number | null;
  totalRevenueUsd: number | null;
  fnbRevenueBs: number;
  nochesDiamond: number;
  nochesComp: number;
  nochesHouse: number;
  ocupadasSinCh: number;
  ocupadasTotales: number;
  diamondShare: number | null;
  segmentMix: SegmentMix;
}

/** De dónde salió el dato Diamond: carga diaria, libro mensual, o ambos. */
export type DiamondOrigen = 'diario' | 'mensual' | 'mixto' | null;

export interface SummaryResponse {
  range: { from: string; to: string };
  sedeIds: number[];
  cards: Record<string, KpiValue>;
  current: RangeSummary;
  comparison: RangeSummary | null;
  diamondOrigen: DiamondOrigen;
}

export interface DiamondMonth {
  mes: number;
  diamond: number | null;
  comp: number | null;
  house: number | null;
  totalNoches: number | null;
  origen: DiamondOrigen;
}

export interface DiamondYearResponse {
  anio: number;
  sedeIds: number[];
  hastaMes: number;
  months: DiamondMonth[];
  prior: DiamondMonth[];
  total: number | null;
  totalPrior: number | null;
  origen: DiamondOrigen;
}

/** Resultado de importar un libro Excel (noches mensuales o cuentas). */
export interface ExcelImportResult {
  imported: number;
  years: number[];
  sedes: number;
  issues: string[];
  /** Solo el libro de cuentas: cuántas cuentas distintas trajo. */
  cuentas?: number;
  /** Qué libro reconoció el Worker por su forma. */
  libro?: 'cuentas' | 'expedia';
  /** Solo el reporte de Expedia. */
  noches?: number;
  reservas?: number;
  canceladas?: number;
}

/** Producción de una cuenta corporativa en el período (noches de habitación). */
export interface CuentaTotal {
  cuenta: string;
  noches: number;
  /** Fracción 0..1 sobre el total de las cuentas seguidas. */
  share: number | null;
}

export interface CuentaMesPoint {
  mes: number;
  total: number;
  porCuenta: Record<string, number>;
  conDato: boolean;
}

export interface CuentaSedeRow {
  cuenta: string;
  total: number;
  /** Noches por código de sede (CCS, BQTO, …). */
  porSede: Record<string, number>;
}

/** Cobertura del denominador del share: cuántos pares (sede, mes) tienen libro. */
export interface ShareSobreHotel {
  share: number | null;
  noches: number;
  base: number;
  paresConBase: number;
  paresTotales: number;
}

export interface CuentasYearResponse {
  anio: number;
  sedeIds: number[];
  hastaMes: number;
  cards: Record<string, KpiValue>;
  lider: CuentaTotal | null;
  ranking: CuentaTotal[];
  meses: CuentaMesPoint[];
  matriz: CuentaSedeRow[];
  share: ShareSobreHotel;
  sinDatos: boolean;
}

// --- Canales de venta (reporte "Reservation Statistics · Option Source") ---

export const CANALES = ['CDR', 'VIP', 'VTA', 'FRO', 'EXP', 'OTROS'] as const;
export type Canal = (typeof CANALES)[number];

/** Etiqueta en español de cada canal (espejo de CANAL_LABEL del motor). */
export const CANAL_LABEL: Record<Canal, string> = {
  CDR: 'Central de Reservas',
  VIP: 'VIP',
  VTA: 'Ventas',
  FRO: 'Front Desk',
  EXP: 'Expedia',
  OTROS: 'Otros',
};

export interface CanalTotal {
  canal: Canal;
  noches: number;
  /** Fracción 0..1 sobre el total de noches del período. */
  share: number | null;
}

export interface CanalMesPoint {
  mes: number;
  total: number;
  porCanal: Record<Canal, number>;
  conDato: boolean;
}

export interface CanalSedeRow {
  /** Código de sede (CCS, BQTO, …). */
  sede: string;
  total: number;
  porCanal: Record<Canal, number>;
  /** Mix del propio hotel: fracción 0..1 por canal. */
  mix: Record<Canal, number | null>;
}

/** Un tipo de habitación vendido por Expedia en el período. */
export interface RoomTypeTotal {
  roomType: string;
  noches: number;
  reservas: number;
  canceladas: number;
  importeUsd: number;
  /** Fracción 0..1 sobre las noches de Expedia del período. */
  share: number | null;
}

/** Lo que Expedia produjo en un hotel, con SUS ratios (no los de la cadena). */
export interface ExpediaSedeTotal {
  /** Código de sede (CCS, BQTO, …). */
  sede: string;
  noches: number;
  reservas: number;
  canceladas: number;
  importeUsd: number;
  tasaCancelacion: number | null;
  adrUsd: number | null;
  /** Fracción 0..1 sobre las noches de Expedia de la cadena. */
  share: number | null;
  roomTypes: RoomTypeTotal[];
  mesesConDato: number;
}

/**
 * Expedia dentro del panel de canales. Sus noches se TALLAN de «Otros» (ya están
 * en el total del PMS), así que `noches` es también su tramo del mix.
 */
export interface ExpediaBlock {
  noches: number;
  reservas: number;
  canceladas: number;
  importeUsd: number;
  tasaCancelacion: number | null;
  adrUsd: number | null;
  roomTypes: RoomTypeTotal[];
  /** Producción por hotel, de mayor a menor. Ratios propios de cada sede. */
  porSede: ExpediaSedeTotal[];
  /** Pares (sede, mes) donde Expedia no cupo en «Otros»: falsa el tallado. */
  exceso: { sede: string; anio: number; mes: number; expedia: number; otros: number }[];
  /** Pares con Expedia y sin reporte del PMS del que tallarla. */
  sinBasePms: number;
  sinDatos: boolean;
}

/** Un código de source que el mapa no reconoce y que arrastra noches. */
export interface CodigoDesconocido {
  source: string;
  noches: number;
}

export interface CanalesYearResponse {
  anio: number;
  sedeIds: number[];
  hastaMes: number;
  cards: Record<string, KpiValue>;
  porCanal: CanalTotal[];
  total: number;
  meses: CanalMesPoint[];
  matriz: CanalSedeRow[];
  desconocidos: CodigoDesconocido[];
  /** Reportes cuyas sources no suman su Grand Total: fallo de lectura. */
  descuadres: { sede: string; anio: number; mes: number; suma: number; grandTotal: number }[];
  expedia: ExpediaBlock;
  sinDatos: boolean;
}

// --- Tipos de habitación (reporte "Statistics - Room Type") ---

export interface RoomTypeTotalRange {
  roomType: string;
  descripcion: string | null;
  noches: number;
  disponibles: number;
  personas: number;
  roomRevenueBs: number;
  /** Fracción 0..1: ocupación DEL TIPO sobre su propio inventario. */
  occPct: number | null;
  adrBs: number | null;
  personasPorNoche: number | null;
  /** Fracción 0..1 sobre las noches de los tipos reportados. */
  share: number | null;
  dias: number;
}

// --- Rate codes del Reservation Statistics 2 (diamond_detalle) ---

/** Un rate code del reporte con lo que produjo en el rango. */
export interface MarketTotal {
  market: string;
  rooms: number;
  /** Fracción 0..1 sobre las noches de los códigos (sin el Grand Total). */
  share: number | null;
  esDiamond: boolean;
}

export interface MarketsResponse {
  from: string;
  to: string;
  sedeIds: number[];
  porMarket: MarketTotal[];
  total: number;
  grandTotal: number | null;
  diamond: number;
  diamondShare: number | null;
  dias: number;
  /** Días donde Σ códigos ≠ Grand Total impreso: fallo de LECTURA del reporte. */
  descuadres: { sedeId: number; fecha: string; suma: number; grandTotal: number }[];
  lider: MarketTotal | null;
  cards: { nochesMarketLider: KpiValue };
  sinDatos: boolean;
}

// --- Movimiento diario y pace (registro_diario) ---

export interface Movimiento {
  walkin: number;
  dayUse: number;
  noShow: number;
  earlyDeparture: number;
  extendedStay: number;
  reservationsMade: number;
  cancellations: number;
}

/** La última foto del pace: es un %, y además mira hacia adelante. */
export interface Pace {
  fecha: string | null;
  next7: number | null;
  next31: number | null;
  next365: number | null;
  sedes: number;
}

export interface OperacionResponse {
  from: string;
  to: string;
  sedeIds: number[];
  movimiento: Movimiento;
  avgPersonRateBs: number | null;
  pace: Pace;
  dias: number;
  cards: Record<string, KpiValue>;
  sinDatos: boolean;
}

/** Una fila = un hotel en el día elegido (`/dashboard/ocupacion-dia`). */
export interface OcupacionSedeDia {
  sedeId: number;
  codigo: string;
  nombre: string;
  ocupadasSinCh: number | null;
  house: number | null;
  comp: number | null;
  ocupadasTotales: number | null;
  totalRooms: number | null;
  baseOrigen: 'flash' | 'catalogo' | 'ninguno';
  ooo: number | null;
  oos: number | null;
  disponibles: number | null;
  occPct: number | null;
  /** Sin flash cargado ese día. No es un 0 %: es un hueco. */
  sinDato: boolean;
}

export interface OcupacionDiaResponse {
  fecha: string;
  sedeIds: number[];
  rows: OcupacionSedeDia[];
  cadena: {
    ocupadasSinCh: number;
    house: number;
    comp: number;
    ocupadasTotales: number;
    totalRooms: number;
    disponibles: number;
    occPct: number | null;
    sedesConDato: number;
    sedesPedidas: number;
  };
  disponible: { min: string; max: string } | null;
  /** La fecha pedida no tenía dato y el API cayó al último día cargado. */
  ajustada: boolean;
}

export interface HabitacionesResponse {
  from: string;
  to: string;
  sedeIds: number[];
  noches: number;
  disponibles: number;
  roomRevenueBs: number;
  occPct: number | null;
  adrBs: number | null;
  adrUsd: number | null;
  tipos: RoomTypeTotalRange[];
  lider: RoomTypeTotalRange | null;
  dias: number;
  /** Noches de estos tipos sobre las ocupadas del hotel: la cobertura. */
  coberturaHotel: number | null;
  nochesHotel: number | null;
  cards: Record<string, KpiValue>;
  sinDatos: boolean;
}

export interface MonthlyRollup {
  sedeId: number;
  anio: number;
  mes: number;
  nochesDiamond: number;
  nochesComp: number;
  nochesHouse: number;
  ocupadasSinCh: number;
  totalNochesHotel: number;
  roomRevenueBs: number;
  roomRevenueUsd: number | null;
  totalRevenueUsd: number | null;
  adrUsd: number | null;
  revparUsd: number | null;
  occPct: number | null;
}

export interface ChainMonthPoint {
  mes: number;
  occPct: number | null;
  adrUsd: number | null;
  revparUsd: number | null;
  roomRevenueUsd: number | null;
  totalRevenueUsd: number | null;
  nochesDiamond: number;
}

/** Chain-level daily point with raw components (re-bucketed client-side by timeframe). */
export interface ChainDailyPoint {
  fecha: string;
  diamond: number;
  house: number;
  comp: number;
  roomsSold: number;
  roomsAvailable: number;
  roomRevenueUsd: number | null;
  totalRevenueUsd: number | null;
  totalRevenueBs: number;
}

export interface GrowthRow {
  sedeId: number;
  codigo: string;
  occ: { current: number | null; prior: number | null; growth: number | null };
  adrUsd: { current: number | null; prior: number | null; growth: number | null };
  revparUsd: { current: number | null; prior: number | null; growth: number | null };
  totalRevenueUsd: { current: number | null; prior: number | null; growth: number | null };
  nochesDiamond: { current: number | null; prior: number | null; growth: number | null };
}

export interface ProjectionResult {
  method: string;
  value: number | null;
  assumptions: string[];
  bandLow?: number | null;
  bandHigh?: number | null;
}

export interface MonthCloseProjection {
  metric: string;
  anio: number;
  mes: number;
  daysElapsed: number;
  mtd: number;
  runRate: ProjectionResult;
  seasonal: ProjectionResult;
}

export interface Alert {
  code: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  context?: Record<string, unknown>;
}

export interface Sede {
  id: number;
  codigo: string;
  nombre: string;
}

export interface Ingesta {
  id: string;
  sede_id: number | null;
  /** Jornada del reporte. Los libros mensuales NO tienen una sola: van en null. */
  fecha_operacion: string | null;
  estado: string;
  origen: string;
  /** 'flash' | 'diamond' | 'source' | 'roomtype' (PDF) · 'cuentas' | 'expedia' (Excel) */
  tipo: string;
  /** Nombre original del archivo, tal como lo subió el operador. */
  filename: string | null;
  /** Resultado del import de un libro Excel, en JSON. Null en los PDF. */
  resumen: string | null;
  error_msg: string | null;
  uploaded_at: string;
}

/** Editable Reservation Statistics 2 extraction (mirror del esquema del Worker). */
export interface ResStatExtraction {
  sede_codigo: string | null;
  formato?: 'semanal' | 'mtd' | null;
  fechas: string[];
  rate_codes: { code: string; rooms: (number | null)[] }[];
  grand_total_rooms: (number | null)[];
  /** Fechas ya cargadas que el revisor decide re-escribir (por defecto, ninguna). */
  sobreescribir?: string[];
}

export interface ResStatCommitResult {
  committed: boolean;
  issues?: string[];
  sedeId?: number;
  days?: { fecha: string; diamond: number }[];
  /** Días que ya estaban cargados y se conservaron. */
  omitidos?: string[];
  error?: string;
}

/** Extracción editable del reporte por tipo de habitación (espejo del Worker). */
export interface RoomTypeStatExtraction {
  sede_codigo: string | null;
  room_type: string | null;
  descripcion: string | null;
  dias: {
    fecha: string;
    potential: number | null;
    ocupadas: number | null;
    personas: number | null;
    room_revenue_bs: number | null;
  }[];
  grand_total: {
    potential: number | null;
    ocupadas: number | null;
    personas: number | null;
    room_revenue_bs: number | null;
  } | null;
}

export interface RoomTypeCommitResult {
  committed: boolean;
  issues?: string[];
  sedeId?: number;
  roomType?: string;
  descripcion?: string | null;
  desde?: string;
  hasta?: string;
  dias?: number;
  suma?: { potential: number; ocupadas: number; personas: number; roomRevenueBs: number };
  grandTotal?: {
    potential: number;
    ocupadas: number;
    personas: number;
    roomRevenueBs: number;
  } | null;
  diasConBlanco?: string[];
  error?: string;
}

/**
 * Extracción editable del reporte por Source (espejo del esquema del Worker).
 * Es MENSUAL: no tiene días, tiene período.
 */
export interface SourceStatExtraction {
  sede_codigo: string | null;
  anio: number | null;
  mes: number | null;
  sources: { code: string; rooms: number | null }[];
  grand_total_rooms: number | null;
  /** El mes ya cargado se reemplaza entero; hay que pedirlo explícitamente. */
  sobreescribir?: boolean;
}

export interface SourceCommitResult {
  committed: boolean;
  issues?: string[];
  sedeId?: number;
  anio?: number;
  mes?: number;
  porCanal?: Record<Canal, number>;
  total?: number;
  grandTotal?: number | null;
  desconocidos?: { code: string; canal: Canal; rooms: number }[];
  /** Se pisó un mes que ya estaba cargado. */
  reemplazo?: boolean;
  /** Lo que ya había cargado, cuando el commit se detiene a preguntar. */
  yaCargado?: { anio: number; mes: number; noches: number };
  error?: string;
}

/** Editable extraction (mirror of the Gemini schema subset used at commit). */
export interface FlashExtraction {
  sede_codigo: string | null;
  fecha_operacion: string | null;
  day: {
    total_rooms?: number | null;
    ooo?: number | null;
    oos?: number | null;
    ocupadas_sin_ch?: number | null;
    house?: number | null;
    comp?: number | null;
    rooms_occupied?: number | null;
    adr_bs?: number | null;
    room_revenue_bs?: number | null;
    total_revenue_bs?: number | null;
    fnb_revenue_bs?: number | null;
    revpar_bs?: number | null;
  };
}

export interface ValidationIssue {
  code: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  field?: string;
}

export interface CommitResult {
  committed: boolean;
  issues: ValidationIssue[];
  derived?: Record<string, unknown>;
  sedeId?: number;
  fecha?: string;
}

export const api = {
  me: () => request<{ user: SessionUser | null }>('/auth/me'),
  login: (email: string, password: string, turnstileToken?: string) =>
    request<{ user: SessionUser }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, turnstileToken }),
    }),
  logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),
  sedes: () => request<{ sedes: Sede[] }>('/sedes'),
  summary: (params: { from?: string; to?: string; sedes?: string; compare?: boolean }) => {
    const q = new URLSearchParams();
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.sedes) q.set('sedes', params.sedes);
    if (params.compare === false) q.set('compare', 'false');
    return request<SummaryResponse>(`/dashboard/summary?${q.toString()}`);
  },
  occupancyTrend: (params: { from?: string; to?: string; sedes?: string }) => {
    const q = qs(params);
    return request<{ points: { fecha: string; occPct: number | null }[] }>(
      `/dashboard/occupancy-trend?${q}`,
    );
  },
  monthly: (params: { anio: number; sedes?: string }) =>
    request<{ anio: number; rollups: MonthlyRollup[] }>(`/dashboard/monthly?${qs(params)}`),
  monthlyChain: (params: { anio: number; sedes?: string }) =>
    request<{ anio: number; points: ChainMonthPoint[] }>(`/dashboard/monthly-chain?${qs(params)}`),
  diamondYear: (params: { anio: number; sedes?: string }) =>
    request<DiamondYearResponse>(`/dashboard/diamond?${qs(params)}`),
  cuentasYear: (params: { anio: number; sedes?: string }) =>
    request<CuentasYearResponse>(`/dashboard/cuentas?${qs(params)}`),
  canalesYear: (params: { anio: number; sedes?: string }) =>
    request<CanalesYearResponse>(`/dashboard/canales?${qs(params)}`),
  habitaciones: (params: { from: string; to: string; sedes?: string }) =>
    request<HabitacionesResponse>(`/dashboard/habitaciones?${qs(params)}`),
  markets: (params: { from: string; to: string; sedes?: string }) =>
    request<MarketsResponse>(`/dashboard/markets?${qs(params)}`),
  operacion: (params: { from: string; to: string; sedes?: string }) =>
    request<OperacionResponse>(`/dashboard/operacion?${qs(params)}`),
  ocupacionDia: (params: { fecha?: string; sedes?: string }) =>
    request<OcupacionDiaResponse>(`/dashboard/ocupacion-dia?${qs(params)}`),
  chainSeries: (params: { from: string; to: string; sedes?: string }) =>
    request<{ range: { from: string; to: string }; points: ChainDailyPoint[] }>(
      `/dashboard/chain-series?${qs(params)}`,
    ),
  growth: (params: { anio: number; sedes?: string }) =>
    request<{ anio: number; window: { from: string; to: string }; rows: GrowthRow[] }>(
      `/analytics/growth?${qs(params)}`,
    ),
  projectionsMonth: (params: { anio?: number; mes?: number; sedes?: string; metrics?: string }) =>
    request<{ anio: number; mes: number; projections: MonthCloseProjection[] }>(
      `/analytics/projections/month?${qs(params)}`,
    ),
  anomalies: () => request<{ alerts: Alert[] }>('/analytics/anomalies'),
  insights: (params: { from?: string; to?: string; sedes?: string }) =>
    request<{ narrative: string }>(`/analytics/insights?${qs(params)}`),
  tasaLatest: () =>
    request<{ latest: { fecha: string; tasa: number; fuente: string } | null; today: string }>(
      '/tasa/latest',
    ),
  tasaSeries: (params?: { from?: string; to?: string }) =>
    request<{ series: { fecha: string; tasa: number; fuente: string }[] }>(
      `/tasa/series${params && (params.from || params.to) ? `?${qs(params)}` : ''}`,
    ),
  tasaManual: (fecha: string, tasa: number) =>
    request<{ ok: boolean }>('/tasa/manual', {
      method: 'POST',
      body: JSON.stringify({ fecha, tasa }),
    }),

  // Ingesta (§7)
  ingestaUpload: (
    files: File[],
    replace = false,
    tipo: 'flash' | 'diamond' | 'source' | 'roomtype' = 'flash',
  ) => {
    const form = new FormData();
    for (const f of files) form.append('files', f);
    return postForm<{
      uploaded: { id: string; file: string; duplicate: boolean; stored: boolean; estado: string }[];
      queued: number;
    }>(`/ingesta/upload?${qs({ replace: replace || undefined, tipo })}`, form);
  },
  ingestaProcessPending: () =>
    request<{ queued: number }>('/ingesta/process-pending', { method: 'POST' }),
  ingestaRetryFailed: () =>
    request<{ retried: number }>('/ingesta/retry-failed', { method: 'POST' }),
  ingestaDelete: (id: string) =>
    request<{ ok: boolean; deleted: number }>(`/ingesta/${id}`, { method: 'DELETE' }),
  ingestaDeleteFailed: () =>
    request<{ deleted: number }>('/ingesta/delete-failed', { method: 'POST' }),
  ingestaImportCuentasMensual: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return postForm<ExcelImportResult>('/ingesta/cuentas-mensual', form);
  },
  /**
   * Un .xlsx sin decir cuál es. El Worker mira la FORMA del libro: hoy el único
   * que se sube es el de cuentas — el de noches lo genera el sistema — así que
   * un libro de noches se rechaza con su motivo en vez de escribir nada.
   */
  ingestaImportExcel: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return postForm<ExcelImportResult & { libro: 'cuentas' | 'expedia' }>('/ingesta/excel', form);
  },
  ingestaList: (estado?: string) =>
    request<{ ingestas: Ingesta[] }>(`/ingesta${estado ? `?estado=${estado}` : ''}`),
  ingestaGet: (id: string) =>
    request<{
      ingesta: Ingesta;
      extracted:
        | FlashExtraction
        | ResStatExtraction
        | SourceStatExtraction
        | RoomTypeStatExtraction
        | null;
      /** Solo para Reservation Statistics: días con Diamond ya cargado. */
      yaCargados?: { fecha: string; diamond: number }[];
      /** Solo para el reporte por Source: meses ya cargados de esa sede. */
      periodosCargados?: { anio: number; mes: number; noches: number }[];
      /** Solo para el reporte por tipo: rangos ya cargados de esa sede. */
      rangosCargados?: {
        room_type: string;
        desde: string;
        hasta: string;
        dias: number;
        ocupadas: number;
      }[];
    }>(`/ingesta/${id}`),
  ingestaCommitResStat: (id: string, resStat: ResStatExtraction, force = false) =>
    request<ResStatCommitResult>(`/ingesta/${id}/commit${force ? '?force=true' : ''}`, {
      method: 'POST',
      body: JSON.stringify(resStat),
    }),
  ingestaCommitRoomType: (id: string, rt: RoomTypeStatExtraction, force = false) =>
    request<RoomTypeCommitResult>(`/ingesta/${id}/commit${force ? '?force=true' : ''}`, {
      method: 'POST',
      body: JSON.stringify(rt),
    }),
  ingestaCommitSource: (id: string, source: SourceStatExtraction, force = false) =>
    request<SourceCommitResult>(`/ingesta/${id}/commit${force ? '?force=true' : ''}`, {
      method: 'POST',
      body: JSON.stringify(source),
    }),
  ingestaExtract: (id: string) =>
    request<{
      ok: boolean;
      tipo: 'flash' | 'diamond' | 'source' | 'roomtype';
      extracted:
        | FlashExtraction
        | ResStatExtraction
        | SourceStatExtraction
        | RoomTypeStatExtraction;
      sedeId: number | null;
    }>(`/ingesta/${id}/extract`, { method: 'POST' }),
  ingestaCommit: (id: string, flash: FlashExtraction, force = false) =>
    request<CommitResult>(`/ingesta/${id}/commit${force ? '?force=true' : ''}`, {
      method: 'POST',
      body: JSON.stringify(flash),
    }),
  ingestaFileUrl: (id: string) => getObjectUrl(`/ingesta/${id}/file`),
  ingestaMissing: (fecha: string) =>
    request<{ fecha: string; missing: { id: number; codigo: string; nombre: string }[] }>(
      `/ingesta/missing/${fecha}`,
    ),

  // Diamond (§7.5)
  diamondUpsert: (sedeId: number, fecha: string, diamond: number) =>
    request<{ ok: boolean; warning: string | null }>('/diamond', {
      method: 'POST',
      body: JSON.stringify({ sedeId, fecha, diamond }),
    }),

  // Export (§2.4)
  exportWeekly: (from: string) => download(`/export/weekly?from=${from}`, `semana_${from}.xlsx`),
  exportMonthly: (anio: number) =>
    download(`/export/monthly?anio=${anio}`, `totalizacion_${anio}.xlsx`),
  exportMonthlyPdf: (anio: number) =>
    download(`/export/monthly.pdf?anio=${anio}`, `totalizacion_${anio}.pdf`),
  exportLibroNoches: (anio: number) =>
    download(`/export/noches?anio=${anio}`, `noches_diamond_${anio}.xlsx`),
  exportLibroNochesPdf: (anio: number) =>
    download(`/export/noches.pdf?anio=${anio}`, `noches_diamond_${anio}.pdf`),
  exportCuentas: (anio: number) =>
    download(`/export/cuentas?anio=${anio}`, `cuentas_corporativas_${anio}.xlsx`),
  exportCuentasPdf: (anio: number) =>
    download(`/export/cuentas.pdf?anio=${anio}`, `cuentas_corporativas_${anio}.pdf`),
  exportCanales: (anio: number) =>
    download(`/export/canales?anio=${anio}`, `canales_venta_${anio}.xlsx`),
  exportCanalesPdf: (anio: number) =>
    download(`/export/canales.pdf?anio=${anio}`, `canales_venta_${anio}.pdf`),
  exportExpedia: (anio: number) => download(`/export/expedia?anio=${anio}`, `expedia_${anio}.xlsx`),
  exportExpediaPdf: (anio: number) =>
    download(`/export/expedia.pdf?anio=${anio}`, `expedia_${anio}.pdf`),
  exportMarkets: (from: string, to: string) =>
    download(`/export/markets?from=${from}&to=${to}`, `rate_codes_${from}_${to}.xlsx`),
  exportMarketsPdf: (from: string, to: string) =>
    download(`/export/markets.pdf?from=${from}&to=${to}`, `rate_codes_${from}_${to}.pdf`),
  exportOperacion: (from: string, to: string) =>
    download(`/export/operacion?from=${from}&to=${to}`, `movimiento_${from}_${to}.xlsx`),
  exportOperacionPdf: (from: string, to: string) =>
    download(`/export/operacion.pdf?from=${from}&to=${to}`, `movimiento_${from}_${to}.pdf`),
  exportHabitaciones: (from: string, to: string) =>
    download(`/export/habitaciones?from=${from}&to=${to}`, `tipos_habitacion_${from}_${to}.xlsx`),
  exportHabitacionesPdf: (from: string, to: string) =>
    download(
      `/export/habitaciones.pdf?from=${from}&to=${to}`,
      `tipos_habitacion_${from}_${to}.pdf`,
    ),
  /**
   * El libro con los bloques marcados. `bloques` viaja siempre explícito —aunque
   * estén todos— para que la descarga diga exactamente qué se pidió; el rango
   * solo hace falta si va el bloque de tipos de habitación.
   */
  exportCompleto: (anio: number, bloques: readonly string[], from: string, to: string) =>
    download(
      `/export/completo?anio=${anio}&bloques=${bloques.join(',')}&from=${from}&to=${to}`,
      `reporte_completo_${anio}.xlsx`,
    ),
  /** El mismo reporte en PDF: mismos bloques, mismas cifras, un solo documento. */
  exportCompletoPdf: (anio: number, bloques: readonly string[], from: string, to: string) =>
    download(
      `/export/completo.pdf?anio=${anio}&bloques=${bloques.join(',')}&from=${from}&to=${to}`,
      `reporte_completo_${anio}.pdf`,
    ),
};

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined) q.set(k, String(v));
  return q.toString();
}
